import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuy.website';

interface ArticleLayoutProps {
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
  imageUrl: string;
  imageAlt: string;
  schemaHeadline: string;
  schemaDescription: string;
  children: ReactNode;
}

export default function ArticleLayout({
  title,
  description,
  date,
  readTime,
  category,
  imageUrl,
  imageAlt,
  schemaHeadline,
  schemaDescription,
  children,
}: ArticleLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-24 md:pt-28">
        <article className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">

            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8">
              <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <Link to="/" className="hover:text-foreground-700 transition-colors">Articles</Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-600 truncate max-w-[200px]">{title}</span>
            </nav>

            {/* Article Header */}
            <header className="mb-10">
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400 mb-4">
                <span>{date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-primary-500 font-medium">{category}</span>
              </div>

              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-900 leading-tight mb-6">
                {title}
              </h1>

              <p className="text-lg text-foreground-500 leading-relaxed">
                {description}
              </p>

              {/* Featured Image */}
              <div className="mt-8 rounded-3xl overflow-hidden">
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  title={imageAlt}
                  className="w-full h-auto object-cover"
                />
              </div>
            </header>

            {/* Article Body */}
            <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">

              {/* Back to home link */}
              <p className="text-sm text-foreground-400">
                <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">
                  &larr; Back to Vigorbuy Spreadsheet Directory
                </Link>
              </p>

              {/* Unique content */}
              {children}

              {/* Final CTA */}
              <section className="bg-primary-500 rounded-3xl p-8 md:p-12 text-center mt-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                  Ready to Start Shopping?
                </h2>
                <p className="text-white/85 text-base mb-6 max-w-lg mx-auto">
                  Explore the latest <strong className="font-semibold text-white">Vigorbuy Spreadsheet</strong> today. Browse thousands of products, use our guides, and shop with confidence.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary-600 font-semibold text-sm hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Explore Spreadsheet
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-white/40 text-white font-semibold text-sm hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
                  >
                    Back to Directory
                  </Link>
                </div>
              </section>

              {/* Back to home */}
              <div className="text-center pt-6 pb-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors duration-200"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </span>
                  Return to Vigorbuy Spreadsheet Directory
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: schemaHeadline,
            description: schemaDescription,
            image: imageUrl,
            datePublished: '2026-06-25',
            dateModified: '2026-06-29',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${siteUrl}${location.pathname}`,
            },
            author: { '@type': 'Organization', name: 'Vigorbuy', url: siteUrl },
            publisher: { '@type': 'Organization', name: 'Vigorbuy', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg' } },
          }),
        }}
      />
    </>
  );
}